/**
 * @praesto/core — app-wide "active hotel" selection.
 *
 * A single hotel the user is currently working in, shared across every module
 * (so a multi-hotel owner picks once and the whole app follows). Lives in the
 * React layer — NOT in the `OrganizationService` data contract: `reachable`
 * hotels are derived from the already-public `membershipsForUser` + `hotels`.
 *
 * The active hotel is always a single concrete hotel (no "all hotels"
 * sentinel). Naturally cross-hotel views iterate `reachable` themselves.
 *
 * The selection is held in React state for the session only — apps/web rule 6
 * forbids persisting tenant data to localStorage. `pickActiveHotel` already
 * accepts a `persistedId` so a future Firestore-backed user preference can seed
 * it across reloads without touching this contract.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Hotel, HotelId, Membership } from "@praesto/shared-types";
import { useCore } from "./react";

/** Decide the active hotel id from the reachable hotels + an optional prior choice. */
export function pickActiveHotel(
  reachable: ReadonlyArray<Hotel>,
  persistedId: HotelId | null,
): HotelId | null {
  if (reachable.length === 0) return null;
  if (persistedId && reachable.some((x) => x.id === persistedId)) return persistedId;
  return reachable[0]!.id;
}

/**
 * The hotels a user can switch between. A `tenant_owner` owns the whole tenant,
 * so they reach EVERY hotel regardless of which per-hotel memberships exist —
 * this mirrors the backend, which skips per-hotel scoping for owners (see the
 * `/users` handler: `globalRole === "tenant_owner"` bypasses the hotel filter).
 * Everyone else reaches only the hotels they hold a membership in.
 *
 * Without the owner short-circuit, an owner seeded into a single hotel (or any
 * owner whose membership rows don't enumerate every hotel) would wrongly see
 * the hotel switcher hidden — the frontend and backend would disagree on scope.
 */
export function reachableHotels(
  memberships: ReadonlyArray<Membership>,
  allHotels: ReadonlyArray<Hotel>,
): ReadonlyArray<Hotel> {
  if (memberships.some((m) => m.role === "tenant_owner")) return allHotels;
  const allowed = new Set(memberships.map((m) => m.hotelId));
  return allHotels.filter((x) => allowed.has(x.id));
}

export interface ActiveHotelValue {
  readonly hotelId: HotelId | null;
  readonly hotel: Hotel | null;
  readonly reachable: ReadonlyArray<Hotel>;
  readonly loading: boolean;
  readonly setActiveHotel: (id: HotelId) => void;
}

const Ctx = createContext<ActiveHotelValue | null>(null);

export function ActiveHotelProvider({ children }: { children: ReactNode }) {
  const core = useCore();
  // Track the signed-in user reactively. Reading currentUserId() once at render
  // would miss the resolution that happens after this provider mounts (it wraps
  // the boot tree, so a child re-render does not re-render the provider).
  const [userId, setUserId] = useState<string | null>(() => core.auth.currentUserId());
  const [reachable, setReachable] = useState<ReadonlyArray<Hotel>>([]);
  const [hotelId, setHotelId] = useState<HotelId | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => core.auth.onAuthChange((u) => setUserId(u?.id ?? null)), [core]);

  useEffect(() => {
    if (!userId) {
      setReachable([]);
      setHotelId(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const ms = await core.organization.membershipsForUser(userId);
      const all = await core.organization.hotels();
      const hotels = reachableHotels(ms, all);
      if (cancelled) return;
      setReachable(hotels);
      // Seed from the prior in-session choice if it is still reachable, else the
      // first reachable hotel (no localStorage — apps/web rule 6).
      setHotelId((prev) => pickActiveHotel(hotels, prev));
    })()
      .catch(() => {
        if (!cancelled) {
          setReachable([]);
          setHotelId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [core, userId]);

  const value = useMemo<ActiveHotelValue>(() => {
    const hotel = reachable.find((x) => x.id === hotelId) ?? null;
    return {
      hotelId,
      hotel,
      reachable,
      loading,
      setActiveHotel: (id: HotelId) => setHotelId(id),
    };
  }, [hotelId, reachable, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveHotel(): ActiveHotelValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useActiveHotel must be used within <ActiveHotelProvider>");
  return v;
}
